/* HUENIT LAB 2.0 — Admin: 블록형 리치 텍스트 에디터.
   blocks: [{id, type, text?|url?|key?|file?}] 배열. type: h|p|li|quote|code|image|video
   image 블록은 URL 대신 S3 key 참조(DESIGN §2.3/§G-5) — 삽입 시 즉시 업로드·dedup. */
import { useRef, useState, useEffect } from 'react';
import { Icon } from '../icons/Icon.jsx';
import { blk, move } from '../data/store.js';
import { useDragSort } from './useDragSort.js';
import { useToast } from './Toast.jsx';
import { useSession } from '../auth/session.jsx';
import { uploadGuideImage } from '../features/guide/uploadGuideImage.js';
import { uploadGuideAsset } from '../features/guide/uploadGuideAsset.js';
import { CODE_BUCKET_URL } from '../config/env.js';

const IMG_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.svg,image/*';
const isImageFile = (f) => !!f && (/^image\//.test(f.type) || /\.(png|jpe?g|gif|webp|svg)$/i.test(f.name || ''));

/* 자동 확장 textarea — 내용 높이만큼 늘어나고 스크롤바 없음.
   value 변화마다 height 재계산(auto→scrollHeight): 타이핑·줄 삭제·언어 탭 전환·
   서버에서 불러온 여러 줄 본문의 최초 렌더까지 모두 맞는 높이가 된다. */
function AutoTextarea({ value, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [value]);
  return <textarea ref={ref} rows={1} value={value} style={{ overflow: 'hidden' }} {...props} />;
}

const BLOCK_TYPES = [
  { type: 'h',     label: '제목',   icon: 'menu' },
  { type: 'p',     label: '본문',   icon: 'file' },
  { type: 'li',    label: '목록',   icon: 'blocks' },
  { type: 'quote', label: '인용',   icon: 'book' },
  { type: 'code',  label: '코드 파일', icon: 'code' },
  { type: 'huenit', label: '.huenit 첨부', icon: 'puzzle' },
  { type: 'image', label: '이미지', icon: 'open' },
  { type: 'video', label: '영상',   icon: 'live' },
];

/* 코드 블록 — 직접 입력 대신 전용 코드 파일(.hbk/.py)을 업로드 */
function CodeFileBlock({ block, update }) {
  const ref = useRef(null);
  const kind = block.kind || 'block';
  const accept = kind === 'python' ? '.py' : '.hbk,.json';
  return (
    <div className="bed-code">
      <div className="seg" style={{ marginBottom: 8 }}>
        {[{ v: 'block', l: '블록', ic: 'blocks' }, { v: 'python', l: 'Python', ic: 'python' }].map(o => (
          <button key={o.v} type="button" className={"seg__opt" + (kind === o.v ? " is-on" : "")} onClick={() => update(block.id, { kind: o.v, file: '' })}>
            <Icon name={o.ic} size={14} />{o.l}
          </button>
        ))}
      </div>
      {block.file ? (
        <div className="dropzone__file">
          <Icon name={kind === 'python' ? 'python' : 'blocks'} size={16} />
          <span className="dropzone__file-name">{block.file}</span>
          <button type="button" className="icon-btn icon-btn--ghost" onClick={() => update(block.id, { file: '' })}><Icon name="close" size={15} /></button>
        </div>
      ) : (
        <button type="button" className="bed-codeup" onClick={() => ref.current && ref.current.click()}>
          <Icon name="upload" size={15} />전용 코드 파일 올리기 ({kind === 'python' ? '.py' : '.hbk'})
        </button>
      )}
      <input ref={ref} type="file" accept={accept} hidden onChange={e => { const f = e.target.files[0]; if (f) update(block.id, { file: f.name }); }} />
    </div>
  );
}

/* .huenit 첨부 블록 — 파일 선택 → S3 업로드(dedup) → block{key,name,sha256,bytes} 저장.
   앱(W145 §계약)은 이 키의 .huenit 를 내려받아 "코드 탭에서 확인하기" 버튼으로 연다. */
function HuenitBlock({ block, update }) {
  const toast = useToast();
  const { getCredentials } = useSession();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (f) => {
    if (!f) return;
    if (!/\.huenit$/i.test(f.name || '')) { toast('.huenit 파일만 올릴 수 있습니다.', 'error'); return; }
    setBusy(true);
    try {
      const { key, sha256, bytes } = await uploadGuideAsset(getCredentials, f);
      update(block.id, { key, name: f.name, sha256, bytes });
    } catch (e) {
      toast('첨부 업로드 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bed-code">
      {block.key ? (
        <div className="dropzone__file">
          <Icon name="puzzle" size={16} />
          <span className="dropzone__file-name">{block.name || (block.key.split('/').pop())}</span>
          <button type="button" className="bed-codeup" style={{ marginLeft: 'auto', width: 'auto' }} disabled={busy}
                  onClick={() => ref.current && ref.current.click()}>
            <Icon name="upload" size={14} />{busy ? '업로드 중…' : '교체'}
          </button>
        </div>
      ) : (
        <button type="button" className="bed-codeup" disabled={busy} onClick={() => ref.current && ref.current.click()}>
          <Icon name="upload" size={15} />{busy ? '업로드 중…' : '.huenit 파일 올리기'}
        </button>
      )}
      <input ref={ref} type="file" accept=".huenit" hidden
             onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
}

/* 이미지 블록 — 파일 선택/드롭 → S3 업로드(dedup) → block.key 저장. 미리보기는 익명 read URL.
   레거시(.url 만 있는) 블록은 url 로 렌더(하위호환)하되 key 가 있으면 key 우선. */
function ImageBlock({ block, update }) {
  const toast = useToast();
  const { getCredentials } = useSession();
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const src = block.key ? (CODE_BUCKET_URL + '/' + block.key) : (block.url || '');

  const upload = async (f) => {
    if (!f) return;
    if (!isImageFile(f)) { toast('이미지 파일만 올릴 수 있습니다.', 'error'); return; }
    setBusy(true);
    try {
      const { key } = await uploadGuideImage(getCredentials, f);
      // key 저장 + 레거시 url 제거(image 블록은 key 만 보관, DESIGN §2.3). url:undefined → JSON 직렬화 시 탈락.
      update(block.id, { key, url: undefined });
    } catch (e) {
      toast('이미지 업로드 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {src ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <img src={src} alt={block.alt || ''}
               style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--ink-150)', flexShrink: 0, background: 'var(--ink-50)' }} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input className="bed-in bed-in--p" value={block.alt || ''} placeholder="대체 텍스트(alt)"
                   onChange={e => update(block.id, { alt: e.target.value })} />
            <button type="button" className="bed-codeup" disabled={busy}
                    onClick={() => ref.current && ref.current.click()}>
              <Icon name="upload" size={14} />{busy ? '업로드 중…' : '이미지 교체'}
            </button>
          </div>
        </div>
      ) : (
        <div className={"dropzone" + (over ? " is-over" : "")} style={busy ? { pointerEvents: 'none', opacity: .7 } : undefined}
             onClick={() => { if (!busy && ref.current) ref.current.click(); }}
             onDragOver={e => { e.preventDefault(); setOver(true); }}
             onDragLeave={() => setOver(false)}
             onDrop={e => { e.preventDefault(); setOver(false); if (!busy) upload(e.dataTransfer.files[0]); }}>
          <div className="dropzone__icon"><Icon name={busy ? 'upload' : 'open'} size={22} /></div>
          <div className="dropzone__t">{busy ? '업로드 중…' : '이미지를 끌어다 놓기'}</div>
          <div className="dropzone__d">또는 클릭해서 선택 · PNG/JPG/GIF/WEBP/SVG</div>
        </div>
      )}
      <input ref={ref} type="file" accept={IMG_ACCEPT} hidden
             onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
}

export function BlockEditor({ blocks, onChange }) {
  const list = blocks || [];
  const setList = (next) => onChange(next);
  const update = (id, patch) => setList(list.map(b => b.id === id ? { ...b, ...patch } : b));
  const remove = (id) => setList(list.filter(b => b.id !== id));
  const add = (type) => {
    const init = type === 'image' ? { key: '', alt: '' }
      : type === 'video' ? { url: '' }
      : type === 'code' ? { file: '', kind: 'block' }
      : type === 'huenit' ? { key: '', name: '', sha256: '', bytes: 0 }
      : { text: '' };
    setList([...list, blk(type, init)]);
  };
  const reorder = (from, to) => setList(move(list, from, to));
  const bind = useDragSort(reorder);

  return (
    <div className="bed">
      <div className="bed__list">
        {list.length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--ink-400)', fontSize: 13 }}>
            아래에서 블록을 추가해 내용을 작성하세요.
          </div>
        )}
        {list.map((b, i) => {
          const d = bind(i);
          return (
            <div key={b.id} className={"bed-block" + (d.isDragging ? " is-dragging" : "") + (d.isOver ? " is-dragover" : "")}
                 onDragOver={d.onDragOver} onDragLeave={d.onDragLeave} onDrop={d.onDrop}>
              <div className="bed-block__gutter">
                <span className="bed-block__handle" draggable={d.draggable} onDragStart={d.onDragStart} onDragEnd={d.onDragEnd} title="드래그로 순서 변경">
                  <Icon name="menu" size={14} />
                </span>
                <span className="bed-block__type">{b.type}</span>
              </div>
              <div className="bed-block__content">
                {b.type === 'li' ? (
                  <div className="bed-li">
                    <span className="bed-li__bullet">●</span>
                    <AutoTextarea className="bed-in bed-in--p" value={b.text || ''} placeholder="목록 항목"
                              onChange={e => update(b.id, { text: e.target.value })} />
                  </div>
                ) : b.type === 'code' ? (
                  <CodeFileBlock block={b} update={update} />
                ) : b.type === 'huenit' ? (
                  <HuenitBlock block={b} update={update} />
                ) : b.type === 'image' ? (
                  <ImageBlock block={b} update={update} />
                ) : b.type === 'video' ? (
                  <div className="bed-media">
                    <Icon name="live" size={16} />
                    <input value={b.url || ''} placeholder="YouTube/영상 URL"
                           onChange={e => update(b.id, { url: e.target.value })} />
                  </div>
                ) : (
                  <AutoTextarea
                    className={"bed-in bed-in--" + (b.type === 'h' ? 'h' : b.type === 'quote' ? 'quote' : 'p')}
                    value={b.text || ''}
                    placeholder={b.type === 'h' ? '제목' : b.type === 'quote' ? '인용문' : '본문을 입력하세요'}
                    onChange={e => update(b.id, { text: e.target.value })} />
                )}
              </div>
              <button type="button" className="icon-btn icon-btn--ghost icon-btn--danger bed-block__del" onClick={() => remove(b.id)} title="블록 삭제">
                <Icon name="trash" size={15} />
              </button>
            </div>
          );
        })}
      </div>
      <div className="bed__add">
        {BLOCK_TYPES.map(t => (
          <button key={t.type} type="button" className="bed__add-btn" onClick={() => add(t.type)}>
            <Icon name={t.icon} size={13} />{t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default BlockEditor;
